import { gql, useQuery } from '@apollo/client';
import IconSync from '@apollo/icons/default/IconSync.svg';
import { Alert, Button, ButtonGroup, Heading, Link } from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import classnames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { TranslatedCursorPositionProvider } from 'src/components/charts/helpers/CursorTranslator';
import { StackedTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import { NoData } from 'src/components/common/noData/NoData';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  errorsFilterDefaults,
  useOperationInsightsFilter,
} from 'src/hooks/useInsightsFilter';
import {
  ErrorInsightsStoredState,
  useLocalStorage,
} from 'src/hooks/useLocalStorage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  date,
  getTimestampsInRange,
  resolutionToSeconds,
} from 'src/lib/date/date';
import {
  ErrorInsightsListGroupByColumn,
  ErrorInsightsListItemFragment,
  ErrorInsightsOperationStatsFragment,
  ErrorInsightsQuery,
  ErrorInsightsQueryVariables,
  ErrorInsightsSeverity,
  ErrorInsightsStrategy,
  ErrorInsightsTimeseriesRecordFragment,
  GraphVariantErrorInsightsListItemEdge,
  Resolution,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers';

import {
  buildErrorFiltersForLegendQuery,
  buildErrorFiltersForListQuery,
  buildErrorFiltersForStatsWindowQuery,
} from './federatedErrors/buildErrorFilters';
import { determineLoadingState } from './federatedErrors/determineLoadingState';
import { DrilldownContext } from './federatedErrors/DrilldownContext';
import { ErrorCountBadge } from './federatedErrors/ErrorCountBadge';
import {
  ErroredRequestsChart,
  errorInsightsOperationStatsFragment,
} from './federatedErrors/ErroredRequestsChart';
import { ErroredRequestsPercentChart } from './federatedErrors/ErroredRequestsPercentChart';
import { ErrorGroup } from './federatedErrors/ErrorGroup';
import { groupColorForIndex } from './federatedErrors/errorGroupColors';
import { ErrorsByGroup } from './federatedErrors/ErrorsByGroup';
import { ErrorsChart, ErrorsChartTooltip } from './federatedErrors/ErrorsChart';
import { ErrorsFeedbackForm } from './federatedErrors/ErrorsFeedbackForm';
import { ErrorsLoading } from './federatedErrors/ErrorsLoading';
import { ErrorsViewSelect } from './federatedErrors/ErrorsViewSelect';
import { FilterButton } from './federatedErrors/FilterButton';
import {
  getRouteParamForBreakdownOption,
  getRouteParamForGroupOption,
} from './federatedErrors/getRouteParam';
import { GroupByButton } from './federatedErrors/GroupByButton';
import { Legend, LegendItem } from './federatedErrors/Legend';
import { OperationsDrilldown } from './federatedErrors/OperationsDrilldown';
import {
  BreakdownOption,
  ErrorsView,
  GroupOption,
} from './federatedErrors/sharedTypes';
import { useAccordionState } from './federatedErrors/useAccordionState';
import { useDisplayNameTranslator } from './federatedErrors/useDisplayNameTranslator';

const errorInsightsListItemFragment = gql`
  fragment ErrorInsightsListItemFragment on ErrorInsightsListItem {
    clientName
    clientVersion
    code
    errorCount
    operationId
    operationName
    path
    service
    severity
  }
`;

const errorInsightsTimeseriesRecordFragment = gql`
  fragment ErrorInsightsTimeseriesRecordFragment on ErrorInsightsTimeseriesRecord {
    timestamp
    data {
      code
      service
      errorCount
    }
    strategy
  }
`;

export const errorInsightsQuery = gql`
  query ErrorInsightsQuery(
    $graphId: ID!
    $variant: String!
    $from: Timestamp!
    $to: Timestamp!
    $resolution: Resolution!
    $filter: ErrorInsightsListFilterInput
    $legendFilter: ErrorInsightsListFilterInput
    $statsWindowFilter: ServiceQueryStatsFilter
    $groupBy: [ErrorInsightsListGroupByColumn!]
    $timeseriesGroupBy: ErrorInsightsListGroupByColumn!
  ) {
    graph(id: $graphId) {
      id
      statsWindow(from: $from, to: $to, resolution: $resolution) {
        queryStats(filter: $statsWindowFilter) {
          ...ErrorInsightsOperationStatsFragment
        }
        roundedUpTo
        roundedDownFrom
      }
      variant(name: $variant) {
        id
        hasManagedSubgraphs
        hasSupergraphSchema
        errorInsightsList(
          from: $from
          to: $to
          filter: $filter
          groupBy: $groupBy
        ) {
          edges {
            node {
              ...ErrorInsightsListItemFragment
            }
            cursor
          }
          pageInfo {
            endCursor
            startCursor
          }
          totalCount
        }
        legendItems: errorInsightsList(
          from: $from
          to: $to
          filter: $legendFilter
          groupBy: $groupBy
        ) {
          edges {
            node {
              ...ErrorInsightsListItemFragment
            }
          }
        }
        errorInsightsTimeseries(
          from: $from
          to: $to
          resolution: $resolution
          filter: $filter
          groupBy: $timeseriesGroupBy
        ) {
          roundedDownFrom
          roundedUpTo
          totalErrors
          records {
            ...ErrorInsightsTimeseriesRecordFragment
          }
        }
      }
    }
  }
  ${errorInsightsOperationStatsFragment}
  ${errorInsightsListItemFragment}
  ${errorInsightsTimeseriesRecordFragment}
`;

function groupValueForStatsRecord(
  record: ErrorInsightsTimeseriesRecordFragment,
  groupByDimension: GroupOption,
) {
  switch (groupByDimension) {
    case ErrorInsightsListGroupByColumn.SERVICE:
      return record.data?.service ?? '';
    case ErrorInsightsListGroupByColumn.CODE:
      return record.data?.code ?? '';
    default:
      assertUnreachable(groupByDimension);
  }
}

function groupValueForListItem(
  item: ErrorInsightsListItemFragment,
  groupByDimension: GroupOption,
) {
  switch (groupByDimension) {
    case ErrorInsightsListGroupByColumn.SERVICE:
      return item.service ?? '';
    case ErrorInsightsListGroupByColumn.CODE:
      return item.code ?? '';
    default:
      assertUnreachable(groupByDimension);
  }
}

/**
 * Projects the timeseries data into stacked form, ensuring that
 * it keeps stacks in the same order for each timestamp.
 * @returns array of normalized StackedTimeSeriesData.
 */
function normalizeBarData({
  allGroupValues,
  timeseriesData,
  groupByDimension,
  timeFrom,
  timeTo,
  resolution,
}: {
  allGroupValues: Array<string | null>;
  timeseriesData: ErrorInsightsTimeseriesRecordFragment[];
  groupByDimension: GroupOption;
  timeFrom: Date;
  timeTo: Date;
  resolution: Resolution;
}): StackedTimeSeriesData[] {
  // First zip up the flat list into a timestamp-keyed map of records.
  // Clamp to the correct time boundaries using steps that
  // match our query resolution.
  const resInMs = resolutionToSeconds(resolution) * 1000;
  const recordsByTime = _.groupBy(timeseriesData, (fes) => {
    const truncatedTimestamp = new Date(
      resInMs * Math.floor(new Date(fes.timestamp).valueOf() / resInMs),
    );
    return truncatedTimestamp.toISOString();
  });

  const normalizedTimeSeriesData = getTimestampsInRange(
    timeFrom,
    timeTo,
    resolution,
  ).map((tsKey) =>
    // Loop over all potential timestamps, then loop over all groups.
    // If we find a matching record for timestamp and group, use it,
    // otherwise use a zero-filler.
    ({
      timestamp: tsKey,
      stackCounts: allGroupValues.map((groupValue) => {
        const groupRecords = recordsByTime[tsKey]?.filter(
          (s) => groupValueForStatsRecord(s, groupByDimension) === groupValue,
        );
        return groupRecords && groupRecords.length > 0
          ? {
              label: groupValue ?? '',
              value: groupRecords.reduce(
                (acc, item) => acc + item.data.errorCount,
                0,
              ),
            }
          : {
              label: groupValue ?? '',
              value: 0,
            };
      }),
    }),
  );
  return normalizedTimeSeriesData;
}

/**
 * Converts *StatsWindow data into stacked form.
 * @returns array of normalized StackedTimeSeriesData.
 */
function normalizeBarDataForOperationStats({
  timeseriesData,
  resolution,
  timeFrom,
  timeTo,
}: {
  timeseriesData: ErrorInsightsOperationStatsFragment[];
  timeFrom: Date;
  timeTo: Date;
  resolution: Resolution;
}): StackedTimeSeriesData[] {
  // First zip up the flat list into a timestamp-keyed map of records.
  // Clamp to the correct time boundaries using steps that
  // match our query resolution.
  const resInMs = resolutionToSeconds(resolution) * 1000;
  const recordsByTime = _.groupBy(timeseriesData, (fes) => {
    const truncatedTimestamp = new Date(
      resInMs * Math.floor(new Date(fes.timestamp).valueOf() / resInMs),
    );
    return truncatedTimestamp.toISOString();
  });

  const normalizedTimeSeriesData = getTimestampsInRange(
    timeFrom,
    timeTo,
    resolution,
  ).map((tsKey) => {
    // Loop over all potential timestamps, then loop over all groups.
    // If we find a matching record for timestamp and group, use it,
    // otherwise use a zero-filler.
    const groupRecords = recordsByTime[tsKey];
    return {
      timestamp: tsKey,
      stackCounts:
        groupRecords && groupRecords.length > 0
          ? [
              {
                label: 'Errored requests',
                value: groupRecords.reduce(
                  (acc, item) => acc + item.metrics.requestsWithErrorsCount,
                  0,
                ),
              },
              {
                label: 'Total requests',
                value: groupRecords.reduce(
                  (acc, item) => acc + item.metrics.totalRequestCount,
                  0,
                ),
              },
            ]
          : [
              {
                label: 'Errored requests',
                value: 0,
              },
              {
                label: 'Total requests',
                value: 0,
              },
            ],
    };
  });
  return normalizedTimeSeriesData;
}

function findReasonableResolutionForErrors(diff: number): Resolution {
  if (diff <= moment.duration(1, 'hours').asSeconds()) {
    return Resolution.R1M;
  }
  if (diff <= moment.duration(12, 'hours').asSeconds()) {
    return Resolution.R15M;
  }
  if (diff <= moment.duration(3, 'days').asSeconds()) {
    return Resolution.R1H;
  }
  if (diff <= moment.duration(3, 'weeks').asSeconds()) {
    return Resolution.R6H;
  }
  return Resolution.R1D;
}

export function FederatedErrors() {
  const location = useLocation();
  const { routeParams, setFilterParams, hasActiveFiltersForSubset } =
    useOperationInsightsFilter();
  const trackCustomEvent = useTrackCustomEvent();
  const [storedView, setStoredView] = useLocalStorage('errorInsightsView');
  const groupByDimension =
    ErrorInsightsListGroupByColumn[routeParams.errorsGroupBy ?? 'SERVICE'];
  const breakdownByDimension =
    ErrorInsightsListGroupByColumn[routeParams.errorsBreakdown ?? 'PATH'];
  const timeRange = routeParams.range ?? 'lastDay';
  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    timeRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
    findReasonableResolutionForErrors,
    routeParams.fastModeRange,
  );

  const errorsView = routeParams.errorsView ?? 'graphql';
  const handleViewChange = useCallback(
    (view: ErrorsView) => {
      setStoredView({ ...storedView, errorsView: view });
      setFilterParams(insightsRouteConfig, { errorsView: view });
    },
    [setFilterParams, setStoredView, storedView],
  );
  const handleGroupByChange = useCallback(
    ({
      selectedGroupOption,
      selectedBreakdownOption,
    }: {
      selectedGroupOption: GroupOption;
      selectedBreakdownOption: BreakdownOption;
    }) => {
      setStoredView({
        ...storedView,
        errorsGroupBy: selectedGroupOption,
        errorsBreakdown: selectedBreakdownOption,
      });
      setFilterParams(insightsRouteConfig, {
        errorsGroupBy: ErrorInsightsListGroupByColumn[selectedGroupOption],
        errorsBreakdown:
          ErrorInsightsListGroupByColumn[selectedBreakdownOption],
      });
    },
    [setFilterParams, setStoredView, storedView],
  );
  const { routeState, preferredState } = useMemo(() => {
    const route: ErrorInsightsStoredState = {
      errorsView,
      errorsGroupBy: groupByDimension,
      errorsBreakdown: breakdownByDimension,
    };
    const preferred: ErrorInsightsStoredState = {
      ...{
        errorsView: 'graphql',
        errorsGroupBy: ErrorInsightsListGroupByColumn.SERVICE,
        errorsBreakdown: ErrorInsightsListGroupByColumn.PATH,
      },
      ...route,
      ...storedView,
    };
    return { routeState: route, preferredState: preferred };
  }, [breakdownByDimension, errorsView, groupByDimension, storedView]);
  useEffect(() => {
    if (!_.isEqual(preferredState, routeState)) {
      setFilterParams(insightsRouteConfig, preferredState);
    }
  }, [preferredState, routeState, setFilterParams]);

  const graphRef = useGraphRef();
  const isInBackground = useIsAppInBackgroundContext();

  const { data: currentData, networkStatus } = useQuery<
    ErrorInsightsQuery,
    ErrorInsightsQueryVariables
  >(errorInsightsQuery, {
    variables: {
      graphId: graphRef?.graphId ?? '',
      variant: graphRef?.graphVariant ?? '',
      from: timeFrom,
      to: timeTo,
      resolution,
      filter: buildErrorFiltersForListQuery(routeParams),
      statsWindowFilter: {
        schemaTag: graphRef?.graphVariant ?? '',
        ...buildErrorFiltersForStatsWindowQuery(routeParams),
      },
      legendFilter: buildErrorFiltersForLegendQuery(routeParams),
      groupBy:
        groupByDimension === ErrorInsightsListGroupByColumn.CODE
          ? [ErrorInsightsListGroupByColumn.SEVERITY, groupByDimension]
          : [groupByDimension],
      timeseriesGroupBy: groupByDimension,
    },
    fetchPolicy: 'no-cache',
    skip: !graphRef,
    pollInterval,
    notifyOnNetworkStatusChange: true,
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
  });
  const { translateToDisplayName } = useDisplayNameTranslator();

  const currentView = useRef<{
    data: ErrorInsightsQuery | undefined;
    groupByDimension: GroupOption;
    resolution: Resolution;
  }>({ data: currentData, groupByDimension, resolution });
  if (currentData) {
    currentView.current = { data: currentData, groupByDimension, resolution };
  }

  const {
    data,
    groupByDimension: activeGroupByDimension,
    resolution: activeResolution,
  } = currentView.current;
  const legendErrorGroups = useMemo(
    () =>
      data?.graph?.variant?.legendItems.edges?.filter(
        (
          edge,
        ): edge is NonNullableField<
          GraphVariantErrorInsightsListItemEdge,
          'node'
        > => edge.node !== null,
      ) ?? [],
    [data?.graph?.variant?.legendItems.edges],
  );

  const listErrorGroups = useMemo(
    () =>
      data?.graph?.variant?.errorInsightsList.edges?.filter(
        (
          edge,
        ): edge is NonNullableField<
          GraphVariantErrorInsightsListItemEdge,
          'node'
        > => edge.node !== null,
      ) ?? [],
    [data?.graph?.variant?.errorInsightsList.edges],
  );
  const hasListData = listErrorGroups.length > 0;
  const timeseriesResult = data?.graph?.variant?.errorInsightsTimeseries;
  const hasTimeseriesData =
    !!timeseriesResult && isMinLengthArray(1, timeseriesResult?.records);
  const hasOperationStatsData =
    isMinLengthArray(1, data?.graph?.statsWindow?.queryStats ?? []) &&
    Boolean(
      data?.graph?.statsWindow?.queryStats.find(
        (record) => record.metrics.requestsWithErrorsCount > 0,
      ),
    );
  const operationStatsResult = data?.graph?.statsWindow;
  const totalErrors = timeseriesResult?.totalErrors ?? 0;
  const loadingState = determineLoadingState({
    hasPreviousData: isMinLengthArray(
      1,
      data?.graph?.variant?.errorInsightsList.edges,
    ),
    networkStatus,
  });

  const normalizedTimeSeriesData = useMemo(() => {
    if (!hasListData || !hasTimeseriesData) {
      return [];
    }

    return normalizeBarData({
      allGroupValues: legendErrorGroups.map((edge) =>
        groupValueForListItem(edge.node, activeGroupByDimension),
      ),
      timeseriesData: timeseriesResult?.records,
      groupByDimension: activeGroupByDimension,
      timeFrom: new Date(timeseriesResult.roundedDownFrom),
      timeTo: new Date(timeseriesResult.roundedUpTo),
      resolution: activeResolution,
    });
  }, [
    legendErrorGroups,
    activeGroupByDimension,
    hasListData,
    hasTimeseriesData,
    activeResolution,
    timeseriesResult,
  ]);

  const legendValueToColorMap = new Map<string, string>();
  const legendItems: LegendItem[] = legendErrorGroups.map((edge, idx) => {
    const groupValue = groupValueForListItem(edge.node, activeGroupByDimension);
    const color = groupColorForIndex(idx);
    legendValueToColorMap.set(groupValue, color);
    const routeParamKey = getRouteParamForGroupOption(activeGroupByDimension);
    return {
      title: translateToDisplayName(groupValue, activeGroupByDimension),
      value: groupValue,
      color,
      selected:
        !Array.isArray(routeParams[routeParamKey]) ||
        (routeParams[routeParamKey] ?? []).includes(groupValue),
    };
  });

  const groupByDisplayValue = translateToDisplayName(
    routeParams[getRouteParamForGroupOption(activeGroupByDimension)]?.[0],
    activeGroupByDimension,
  );
  const breakdownByDisplayValue = translateToDisplayName(
    routeParams[getRouteParamForBreakdownOption(breakdownByDimension)]?.[0],
    breakdownByDimension,
  );

  const accordionKeys = useMemo(
    () =>
      listErrorGroups.map((edge) =>
        groupValueForListItem(edge.node, activeGroupByDimension),
      ),
    [activeGroupByDimension, listErrorGroups],
  );
  const { expandedIndices, handleExpandChange } =
    useAccordionState(accordionKeys);

  const handleFilterResetClick = () => {
    // clear local storage when resetting
    setStoredView(undefined);
    setFilterParams(insightsRouteConfig, errorsFilterDefaults);
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_reset_button_click',
    });
  };

  const hasOnlyOperationStatsData =
    !hasTimeseriesData && !hasListData && hasOperationStatsData;

  const normalizedOperationStatsData = operationStatsResult
    ? normalizeBarDataForOperationStats({
        timeseriesData: operationStatsResult.queryStats,
        resolution: activeResolution,
        timeFrom: new Date(operationStatsResult.roundedDownFrom),
        timeTo: new Date(operationStatsResult.roundedUpTo),
      })
    : [];
  const isUsingLegacyStats =
    Boolean(timeseriesResult) &&
    isMinLengthArray(1, timeseriesResult?.records) &&
    !timeseriesResult?.records?.some(({ strategy }) =>
      [
        ErrorInsightsStrategy.FEDERATED_ERROR_STATS,
        ErrorInsightsStrategy.MIXED,
      ].includes(strategy),
    );

  const [hasDismissedUpgradeCTA, dismissUpgradeCTA] = useLocalStorage(
    'hasDismissedErrorInsightsUpgradeCTA',
  );

  const isFederated =
    (data?.graph?.variant?.hasManagedSubgraphs ||
      data?.graph?.variant?.hasSupergraphSchema) ??
    true;
  const showUpgradeCTA =
    !hasDismissedUpgradeCTA &&
    (hasOnlyOperationStatsData || isUsingLegacyStats);

  return (
    <div data-testid="federated-errors" className="pb-8">
      {showUpgradeCTA && (
        <Alert
          status="info"
          className="mb-6"
          isDismissible
          onDismiss={() => dismissUpgradeCTA(true)}
        >
          For more comprehensive error metrics including error codes, upgrade to
          router ≥ 2.1.2 and{' '}
          <Link to={DocsRouter.path('ExtendedErrorConfig')} isExternal>
            enable extended error reporting.
          </Link>{' '}
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ErrorsViewSelect
          currentView={errorsView ?? 'graphql'}
          onViewChange={handleViewChange}
        />
        {/* Drilldown context */}
        {errorsView === 'graphql' && routeParams.errorsDrilldown && (
          <DrilldownContext
            groupByDimension={groupByDimension}
            groupByDimensionValue={groupByDisplayValue}
            breakdownByDimensionValue={breakdownByDisplayValue}
          />
        )}
        {/* Filter button group */}
        {errorsView === 'graphql' && !routeParams.errorsDrilldown && (
          <ButtonGroup
            isDisabled={loadingState !== 'none' || hasOnlyOperationStatsData}
            className={classnames('inline-flex flex-wrap gap-x-1 gap-y-2', {
              'opacity-60': loadingState === 'secondary',
            })}
          >
            <GroupByButton
              selectedGroupOption={groupByDimension}
              selectedBreakdownOption={breakdownByDimension}
              onGroupSelectionChange={handleGroupByChange}
            />
            <FilterButton dimension="service" />
            <FilterButton dimension="code" />
            <Tooltip label="Reset Filters" placement="top">
              <Button
                isDisabled={!hasActiveFiltersForSubset(errorsFilterDefaults)}
                onClick={handleFilterResetClick}
                variant="hidden"
                leftIcon={<IconSync />}
              />
            </Tooltip>
          </ButtonGroup>
        )}
      </div>
      {/* Loading spinner */}
      {loadingState === 'initial' && <ErrorsLoading />}
      {/* GraphQL Errors view */}
      {loadingState !== 'initial' &&
        errorsView === 'graphql' &&
        (hasTimeseriesData ? (
          <CursorProvider>
            <TranslatedCursorPositionProvider data={normalizedTimeSeriesData}>
              <Heading size="lg" className="ml-2 mt-4">
                {isUsingLegacyStats && isFederated
                  ? 'Sampled Errors'
                  : 'Errors'}
                <ErrorCountBadge
                  isFederated={isFederated}
                  isUsingLegacyStats={isUsingLegacyStats}
                  showingOperationStats={false}
                  totalErrors={totalErrors}
                />
              </Heading>
              <ErrorsChart
                data={normalizedTimeSeriesData}
                loading={loadingState === 'secondary'}
                tooltipContent={
                  <ErrorsChartTooltip
                    groupByDimension={activeGroupByDimension}
                    resolution={resolution}
                  />
                }
                groupByDimension={activeGroupByDimension}
              />
              <Legend
                className="flex-wrap gap-1 pb-8"
                items={legendItems}
                groupByDimension={activeGroupByDimension}
                loading={loadingState === 'secondary'}
              />
              {routeParams.errorsDrilldown ? (
                <OperationsDrilldown />
              ) : (
                <ErrorsByGroup
                  expandedIndices={expandedIndices}
                  onExpandChange={handleExpandChange}
                  loading={loadingState === 'secondary'}
                >
                  {listErrorGroups.map((errorGroupEdge, idx) => (
                    <ErrorGroup
                      key={errorGroupEdge.cursor}
                      isExpanded={expandedIndices.includes(idx)}
                      groupByDimension={activeGroupByDimension}
                      groupByDimensionValue={groupValueForListItem(
                        errorGroupEdge.node,
                        activeGroupByDimension,
                      )}
                      groupSeverity={
                        errorGroupEdge.node.severity ??
                        ErrorInsightsSeverity.ERROR
                      }
                      breakdownByDimension={breakdownByDimension}
                      groupColor={
                        legendValueToColorMap.get(
                          groupValueForListItem(
                            errorGroupEdge.node,
                            activeGroupByDimension,
                          ),
                        ) ?? groupColorForIndex(0)
                      }
                      groupErrorCount={errorGroupEdge.node.errorCount}
                    />
                  ))}
                </ErrorsByGroup>
              )}
              <div className="mt-8">
                <ErrorsFeedbackForm />
              </div>
            </TranslatedCursorPositionProvider>
          </CursorProvider>
        ) : (
          /* No data for GraphQL errors */
          <NoData
            className="flex h-80 items-center justify-center"
            message={
              <>
                <Heading size="lg">
                  {hasOnlyOperationStatsData
                    ? 'No GraphQL error data'
                    : 'No errors reported'}
                </Heading>
                <Telescope className="my-2" />
                {hasOnlyOperationStatsData ? (
                  <p className="pt-2">
                    View{' '}
                    <Link
                      to={insightsRouteConfig.locationFrom({
                        location,
                        patch: { errorsView: 'requests' },
                      })}
                    >
                      errored requests
                    </Link>{' '}
                    to see operation error rates.
                  </p>
                ) : (
                  <p>Try adjusting time range or resetting filters.</p>
                )}
              </>
            }
          />
        ))}
      {/* Errored requests view */}
      {loadingState !== 'initial' &&
        errorsView === 'requests' &&
        (hasOperationStatsData ? (
          <CursorProvider>
            <ErroredRequestsChart
              data={normalizedOperationStatsData}
              loading={loadingState === 'secondary'}
              resolution={activeResolution}
            />
            <ErroredRequestsPercentChart
              data={normalizedOperationStatsData}
              loading={loadingState === 'secondary'}
              resolution={activeResolution}
            />
          </CursorProvider>
        ) : (
          /* No data for errored requests */
          <NoData
            className="flex h-80 items-center justify-center"
            message={
              <>
                <Heading size="lg">No errors reported</Heading>
                <Telescope className="my-2" />
                <p>Try adjusting time range or resetting filters.</p>
              </>
            }
          />
        ))}
    </div>
  );
}
